import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageCircle } from "lucide-react";
import webIconSendSvg from '@/assets/web_icon_send.svg';
import { apiClient } from "@/lib/api-client";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from 'react-i18next';
import { useDateFormat } from '@/utils/date-formatter';
import { SYSTEM_USER_ID } from '@mangelapp/shared';

interface ChatMessage {
  id: string;
  ticketId: string;
  userId: string;
  message: string;
  createdAt: string;
  userName: string;
  userCode: string;
  profilePhotoUrl?: string | null;
  isRead: boolean;
}

interface TicketChatProps {
  ticketId: string;
  className?: string;
  onUnreadCountChange?: (count: number) => void;
}

export default function TicketChat({ ticketId, className = "", onUnreadCountChange }: TicketChatProps) {
  const [newMessage, setNewMessage] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const hasMarkedAsRead = useRef(false);
  const { t } = useTranslation(['components.ticketChat']);
  const { formatDateTime } = useDateFormat();

  // Chat-Nachrichten laden with read status
  const { data, isLoading } = useQuery<{ messages: ChatMessage[]; unreadCount: number }>({
    queryKey: ["/api/tickets", ticketId, "chat"],
    enabled: !!ticketId,
    queryFn: async () => {
      return apiClient.getTicketChat(ticketId);
    },
  });

  const messages = data?.messages || [];
  const unreadCount = data?.unreadCount || 0;

  // Mark as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: async () => {
      return apiClient.markTicketChatAsRead(ticketId);
    },
    onSuccess: () => {
      // Invalidate to refresh read status
      queryClient.invalidateQueries({
        queryKey: ["/api/tickets", ticketId, "chat"],
      });
    },
  });

  // Mark messages as read when chat opens (only once per mount)
  useEffect(() => {
    if (!isLoading && ticketId && !hasMarkedAsRead.current && unreadCount > 0) {
      hasMarkedAsRead.current = true;
      markAsReadMutation.mutate();
    }
  }, [isLoading, ticketId, unreadCount]);

  // Reset the flag when ticketId changes
  useEffect(() => {
    hasMarkedAsRead.current = false;
  }, [ticketId]);

  // Notify parent of unread count changes
  useEffect(() => {
    if (onUnreadCountChange) {
      onUnreadCountChange(unreadCount);
    }
  }, [unreadCount, onUnreadCountChange]);

  // Nachricht senden
  const sendMessageMutation = useMutation({
    mutationFn: async (message: string) => {
      return apiClient.sendTicketMessage(ticketId, message);
    },
    onSuccess: () => {
      // Cache aktualisieren
      queryClient.invalidateQueries({
        queryKey: ["/api/tickets", ticketId, "chat"],
      });
      setNewMessage("");
    },
    onError: (error) => {
      console.error("Chat-Fehler:", error);
      toast({
        title: t('components.ticketChat:errorTitle'),
        description: t('components.ticketChat:errorSend'),
        variant: "destructive",
      });
    },
  });

  // Auto-scroll zum Ende bei neuen Nachrichten
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = () => {
    if (newMessage.trim() && !sendMessageMutation.isPending) {
      sendMessageMutation.mutate(newMessage.trim());
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (dateString: string) => {
    return formatDateTime(dateString);
  };

  if (isLoading) {
    return (
      <div className={`bg-white border border-slate-200 rounded-lg ${className}`}>
        <div className="p-3 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <MessageCircle className="h-4 w-4 text-blue-600" />
            <span className="text-xs font-medium text-slate-900">{t('components.ticketChat:title')}</span>
          </div>
        </div>
        <div className="p-4 text-center text-xs text-slate-500">
          {t('components.ticketChat:loading')}
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white border border-slate-200 rounded-lg flex flex-col ${className}`}>
      {/* Header */}
      <div className="p-3 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <MessageCircle className="h-4 w-4 text-blue-600" />
          <span className="text-xs font-medium text-slate-900">{t('components.ticketChat:title')}</span>
          {messages.length > 0 && (
            <span className="text-xs text-slate-500">({messages.length})</span>
          )}
        </div>
      </div>

      {/* Chat-Verlauf */}
      <div className="flex-1 p-3 max-h-64 overflow-y-auto space-y-3">
        {messages.length === 0 ? (
          <div className="text-center text-xs text-slate-500 py-4">
            {t('components.ticketChat:empty.line1')}
            <br />
            {t('components.ticketChat:empty.line2')}
          </div>
        ) : (
          messages.map((message) => {
            const isSystemMessage = message.userId === SYSTEM_USER_ID;
            
            // System messages: centered, no bubble, italic, muted
            if (isSystemMessage) {
              return (
                <div key={message.id} className="flex justify-center py-2">
                  <div className="text-xs text-gray-500 italic text-center max-w-[80%]">
                    {message.message}
                  </div>
                </div>
              );
            }
            
            // Regular user messages with bubble
            return (
              <div key={message.id} className="space-y-1">
                {/* Benutzer und Zeit */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {/* Unread indicator dot */}
                    {!message.isRead && (
                      <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" title={t('components.ticketChat:unread')} />
                    )}
                    <span className="text-xs font-medium text-slate-700">
                      {message.userName || message.userCode || t('components.ticketChat:unknownUser')}
                    </span>
                  </div>
                  <span className="text-xs text-slate-500">
                    {formatTime(message.createdAt)}
                  </span>
                </div>
                {/* Nachricht */}
                <div className={`rounded-lg p-2 ${message.isRead ? 'bg-slate-50 border border-slate-100' : 'bg-blue-50 border border-blue-100'}`}>
                  <p className="text-xs text-slate-900 whitespace-pre-wrap">
                    {message.message}
                  </p>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Eingabefeld */}
      <div className="p-3 border-t border-slate-200">
        <div className="flex gap-2">
          <input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={t('components.ticketChat:inputPlaceholder') as string}
            className="flex-1 h-7 px-4 text-xs bg-white border border-black rounded-full shadow-none focus:outline-none focus:border-black disabled:opacity-50"
            disabled={sendMessageMutation.isPending}
            data-testid="input-chat-message"
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={!newMessage.trim() || sendMessageMutation.isPending}
            className="h-7 w-7 p-0 disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-70 transition-opacity"
            data-testid="button-send-message"
          >
            <img src={webIconSendSvg} alt="Send" className="h-7 w-7" />
          </button>
        </div>
      </div>
    </div>
  );
}
